import { useEffect, useMemo, useState } from 'react';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/hooks/useAuth';
import v2_actionConfigCrm from 'src/actions/v2/crm/v2_actionConfigCrm';
import getActionListaUsuariosByClienteId from 'src/actions/usuarios/getActionListaUsuariosByClienteId';
import { Alert, AlertDescription } from 'src/@/components/ui/alert';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'src/@/components/ui/accordion';
import { Label } from 'src/@/components/ui/label';
import { Switch } from 'src/@/components/ui/switch';
import { Separator } from 'src/@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { RadioGroup, RadioGroupItem } from 'src/@/components/ui/radio-group';
import { MultiSelect } from 'src/@/components/ui/multi-select';

const INPUT_CLS = 'rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20';

export default function CriacaoLeadFunilSection({ aiAgent, changeModuleConfig }) {
    const { cliente } = useAuth();
    const realstate = aiAgent?.config?.module?.realstate || {};
    const funcoes = realstate.funcoes || {};
    const cfg = funcoes.criacaoLeadFunil || {};
    const workflow = realstate.perguntasEssenciais?.workflow || { nodes: [] };

    const totalQualificatorias = useMemo(() => {
        const perguntas = (workflow.nodes || []).flatMap((n) => n.data?.perguntas || []);
        return perguntas.filter((p) => p?.qualificatoria).length;
    }, [workflow.nodes]);

    const [pipelines, setPipelines] = useState([]);
    const [stages, setStages] = useState([]);
    const [origens, setOrigens] = useState([]);
    const [usuarios, setUsuarios] = useState([]);

    async function buscarConfigCrm(pipelineId) {
        if (!cliente?._id) return;
        try {
            const querys = pipelineId ? `?pipeline_id=${pipelineId}` : '';
            const result = await v2_actionConfigCrm(cliente._id, querys);
            const data = result?.data?.data;
            if (!data) return;
            if (data.pipelines) setPipelines(data.pipelines);
            if (data.stages) setStages(data.stages);
            if (data.origens) setOrigens(data.origens);
        } catch (error) {
            console.log(error);
        }
    }

    async function buscarUsuarios() {
        if (!cliente?._id) return;
        try {
            const result = await getActionListaUsuariosByClienteId(cliente._id);
            const lista = result?.data?.usuarios || [];
            setUsuarios(Array.isArray(lista) ? lista : []);
        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        buscarConfigCrm(cfg.pipelineId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cliente?._id, cfg.pipelineId]);

    useEffect(() => {
        buscarUsuarios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cliente?._id]);

    const usuarioOptions = useMemo(
        () => usuarios.map((u) => ({
            value: String(u._id),
            label: u.nomeFantasia || u.nome || u.name || u.email || '(sem nome)',
        })),
        [usuarios],
    );

    const updateCfg = (patch) => {
        changeModuleConfig({
            funcoes: {
                ...funcoes,
                criacaoLeadFunil: { ...cfg, ...patch },
            },
        });
    };

    const modo = cfg.modo || 'primeiro_contato';
    const atribuicao = cfg.atribuicaoUsuario || { tipo: 'nenhum', usuarioId: null, usuariosRoleta: [] };
    const tipoAtribuicao = atribuicao.tipo || 'nenhum';
    const aoExistirAberto = cfg.aoExistirAberto === 'criar_novo' ? 'criar_novo' : 'reutilizar';
    const ativoSemFunil = !!cfg.ativo && (!cfg.pipelineId || !cfg.estagioId);

    const updateAtribuicao = (patch) => {
        updateCfg({
            atribuicaoUsuario: { ...atribuicao, ...patch },
        });
    };

    return (
        <Accordion
            type="single"
            collapsible
            className="rounded-xl border border-gray-100 bg-white"
        >
            <AccordionItem value="criacao-lead" className="border-0">
                <AccordionTrigger className="hover:no-underline px-6 py-4 [&[data-state=open]]:pb-3">
                    <div className="flex items-start justify-between gap-3 flex-1 pr-3 text-left">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50 shrink-0">
                                <Iconify icon="mdi:account-plus-outline" width={20} className="text-violet-600" />
                            </div>
                            <div>
                                <div className="text-base font-semibold text-gray-900 leading-tight">Criação de Card no Funil</div>
                                <div className="text-sm text-gray-500 font-normal mt-1">
                                    Define quando a Lais cria automaticamente um card no funil para que o agente possa alimentá-lo.
                                </div>
                            </div>
                        </div>
                        <div
                            role="presentation"
                            onClick={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            className="shrink-0"
                        >
                            <Switch
                                checked={!!cfg.ativo}
                                onCheckedChange={(value) => updateCfg({ ativo: value })}
                            />
                        </div>
                    </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                    {!cfg.ativo ? (
                        <p className="text-xs text-gray-500 italic">Ative o recurso acima para configurar.</p>
                    ) : (
                <div className="flex flex-col gap-4">
                    {ativoSemFunil && (
                        <Alert className="bg-amber-50 border-amber-200 text-amber-800">
                            <AlertDescription className="text-xs">
                                Selecione um funil e um estágio para a criação automática começar a funcionar.
                            </AlertDescription>
                        </Alert>
                    )}

                    <div className="flex flex-col gap-2">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                            Quando criar o card
                        </Label>
                        <RadioGroup
                            value={modo}
                            onValueChange={(value) => updateCfg({ modo: value })}
                            className="flex flex-col gap-2"
                        >
                            <label
                                htmlFor="modo-primeiro-contato"
                                className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
                                    modo === 'primeiro_contato'
                                        ? 'border-[#7C3AED] bg-violet-50/50'
                                        : 'border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                <RadioGroupItem value="primeiro_contato" id="modo-primeiro-contato" className="mt-1" />
                                <div className="flex flex-col">
                                    <span className="text-sm font-medium text-gray-900">Primeiro contato com o agente</span>
                                    <span className="text-xs text-gray-500">
                                        O card é criado assim que o cliente envia a primeira mensagem para o agente.
                                    </span>
                                </div>
                            </label>
                            <label
                                htmlFor="modo-ao-qualificar"
                                className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
                                    modo === 'ao_qualificar'
                                        ? 'border-[#7C3AED] bg-violet-50/50'
                                        : 'border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                <RadioGroupItem value="ao_qualificar" id="modo-ao-qualificar" className="mt-1" />
                                <div className="flex flex-col">
                                    <span className="text-sm font-medium text-gray-900">
                                        Ao atingir todas as Perguntas Essenciais
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        O card só é criado quando o cliente respondeu todas as perguntas marcadas como qualificatórias
                                        {totalQualificatorias > 0 ? ` (${totalQualificatorias} configurada${totalQualificatorias > 1 ? 's' : ''}).` : '.'}
                                        {totalQualificatorias === 0 && ' Marque perguntas como qualificatórias na aba "Perguntas Essenciais" antes de usar este modo.'}
                                    </span>
                                </div>
                            </label>
                        </RadioGroup>
                    </div>

                    <Separator className="bg-gray-200/60" />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-2">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Funil</Label>
                            <Select
                                value={cfg.pipelineId || ''}
                                onValueChange={(value) => updateCfg({ pipelineId: value, estagioId: null })}
                            >
                                <SelectTrigger className={INPUT_CLS}>
                                    <SelectValue placeholder="Selecione um funil..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {pipelines.map((p) => (
                                        <SelectItem key={p._id} value={p._id}>
                                            {p.nome}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="flex flex-col gap-2">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Estágio</Label>
                            <Select
                                value={cfg.estagioId || ''}
                                onValueChange={(value) => updateCfg({ estagioId: value })}
                                disabled={!cfg.pipelineId}
                            >
                                <SelectTrigger className={INPUT_CLS}>
                                    <SelectValue placeholder={cfg.pipelineId ? 'Selecione um estágio...' : 'Escolha um funil primeiro'} />
                                </SelectTrigger>
                                <SelectContent>
                                    {stages.map((s) => (
                                        <SelectItem key={s._id} value={s._id}>
                                            {s.nome}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="flex flex-col gap-2 max-w-md">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                            Origem do lead <span className="font-normal normal-case text-gray-400">(opcional)</span>
                        </Label>
                        <Select
                            value={cfg.origemId || ''}
                            onValueChange={(value) => updateCfg({ origemId: value || null })}
                        >
                            <SelectTrigger className={INPUT_CLS}>
                                <SelectValue placeholder="Selecione uma origem..." />
                            </SelectTrigger>
                            <SelectContent>
                                {origens.map((o) => (
                                    <SelectItem key={o._id} value={o._id}>
                                        {o.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <Separator className="bg-gray-200/60" />

                    <div className="flex flex-col gap-2">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                            Quando já houver um card aberto para este chat
                        </Label>
                        <RadioGroup
                            value={aoExistirAberto}
                            onValueChange={(value) => updateCfg({ aoExistirAberto: value })}
                            className="flex flex-col gap-2"
                        >
                            <label
                                htmlFor="existe-reutilizar"
                                className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
                                    aoExistirAberto === 'reutilizar'
                                        ? 'border-[#7C3AED] bg-violet-50/50'
                                        : 'border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                <RadioGroupItem value="reutilizar" id="existe-reutilizar" className="mt-1" />
                                <div className="flex flex-col">
                                    <span className="text-sm font-medium text-gray-900">Reutilizar o card existente</span>
                                    <span className="text-xs text-gray-500">
                                        Se já existe um card aberto para o chat (criado pela IA ou manualmente),
                                        a Lais alimenta esse card. Evita duplicação.
                                    </span>
                                </div>
                            </label>
                            <label
                                htmlFor="existe-criar-novo"
                                className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
                                    aoExistirAberto === 'criar_novo'
                                        ? 'border-[#7C3AED] bg-violet-50/50'
                                        : 'border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                <RadioGroupItem value="criar_novo" id="existe-criar-novo" className="mt-1" />
                                <div className="flex flex-col">
                                    <span className="text-sm font-medium text-gray-900">Sempre criar um card novo</span>
                                    <span className="text-xs text-gray-500">
                                        Ignora cards já abertos para o chat e cria sempre um novo no funil
                                        configurado. Pode haver mais de um card aberto por chat.
                                    </span>
                                </div>
                            </label>
                        </RadioGroup>
                    </div>

                    <Separator className="bg-gray-200/60" />

                    <div className="flex flex-col gap-2">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                            Atribuição do responsável
                        </Label>
                        <RadioGroup
                            value={tipoAtribuicao}
                            onValueChange={(value) => updateAtribuicao({ tipo: value })}
                            className="flex flex-col gap-2"
                        >
                            <label
                                htmlFor="atrib-nenhum"
                                className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
                                    tipoAtribuicao === 'nenhum'
                                        ? 'border-[#7C3AED] bg-violet-50/50'
                                        : 'border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                <RadioGroupItem value="nenhum" id="atrib-nenhum" className="mt-1" />
                                <div className="flex flex-col">
                                    <span className="text-sm font-medium text-gray-900">Sem responsável</span>
                                    <span className="text-xs text-gray-500">
                                        O card é criado sem usuário responsável (atribuição manual depois).
                                    </span>
                                </div>
                            </label>
                            <label
                                htmlFor="atrib-fixo"
                                className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
                                    tipoAtribuicao === 'fixo'
                                        ? 'border-[#7C3AED] bg-violet-50/50'
                                        : 'border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                <RadioGroupItem value="fixo" id="atrib-fixo" className="mt-1" />
                                <div className="flex flex-col">
                                    <span className="text-sm font-medium text-gray-900">Usuário fixo</span>
                                    <span className="text-xs text-gray-500">
                                        Todos os cards criados pela IA ficam atribuídos ao mesmo usuário.
                                    </span>
                                </div>
                            </label>
                            <label
                                htmlFor="atrib-roleta"
                                className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
                                    tipoAtribuicao === 'roleta'
                                        ? 'border-[#7C3AED] bg-violet-50/50'
                                        : 'border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                <RadioGroupItem value="roleta" id="atrib-roleta" className="mt-1" />
                                <div className="flex flex-col">
                                    <span className="text-sm font-medium text-gray-900">Roleta entre usuários</span>
                                    <span className="text-xs text-gray-500">
                                        Distribui os cards em rotação (round-robin) entre os usuários selecionados.
                                    </span>
                                </div>
                            </label>
                        </RadioGroup>
                    </div>

                    {tipoAtribuicao === 'fixo' && (
                        <div className="flex flex-col gap-2 max-w-md">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                Usuário responsável
                            </Label>
                            <Select
                                value={atribuicao.usuarioId || ''}
                                onValueChange={(value) => updateAtribuicao({ usuarioId: value || null })}
                            >
                                <SelectTrigger className={INPUT_CLS}>
                                    <SelectValue placeholder="Selecione um usuário..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {usuarioOptions.map((u) => (
                                        <SelectItem key={u.value} value={u.value}>
                                            {u.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {!atribuicao.usuarioId && (
                                <Alert className="bg-amber-50 border-amber-200 text-amber-800">
                                    <AlertDescription className="text-xs">
                                        Selecione um usuário para a atribuição fixa funcionar.
                                    </AlertDescription>
                                </Alert>
                            )}
                        </div>
                    )}

                    {tipoAtribuicao === 'roleta' && (
                        <div className="flex flex-col gap-2">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                Usuários da roleta
                            </Label>
                            <MultiSelect
                                options={usuarioOptions}
                                onValueChange={(values) => updateAtribuicao({ usuariosRoleta: values })}
                                defaultValue={Array.isArray(atribuicao.usuariosRoleta) ? atribuicao.usuariosRoleta : []}
                                placeholder="Selecione os usuários..."
                                variant="inverted"
                                animation={2}
                                maxCount={5}
                            />
                            {(!Array.isArray(atribuicao.usuariosRoleta) || atribuicao.usuariosRoleta.length === 0) && (
                                <Alert className="bg-amber-50 border-amber-200 text-amber-800">
                                    <AlertDescription className="text-xs">
                                        Selecione pelo menos um usuário para a roleta.
                                    </AlertDescription>
                                </Alert>
                            )}
                            {Array.isArray(atribuicao.usuariosRoleta) && atribuicao.usuariosRoleta.length > 0 && (
                                <p className="text-xs text-gray-500">
                                    Cada novo card é atribuído ao próximo usuário da lista, em rotação.
                                </p>
                            )}
                        </div>
                    )}
                </div>
                    )}
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    );
}
